/**
 * Case Management's loading shape.
 *
 * Read this file next to `./table.tsx` — it is that component's layout with the
 * two regions that *need server data* swapped for `<Skeleton />` (the "last
 * updated" timestamp and the table body) and nothing else changed. Everything
 * the page can draw without a response — the toolbar container, the search
 * input, the active-filter chips, the "Last updated:" label, the refresh
 * button — renders here for real, so it doesn't pop into existence when the
 * query resolves (P-05, P-17).
 *
 * Two boundaries worth knowing, because they're where the "render the real
 * chrome" rule stops:
 *
 * 1. **`CaseManagementFilters` is shape-matched, not imported.** It is static
 *    chrome, but it drags `Calendar` + date-fns behind it, and this file is
 *    imported *eagerly* by the route while the real table is `lazy()` — reusing
 *    it here would undo the split (P-21). Reuse real chrome when it's cheap;
 *    reproduce its shape when reuse would move weight into the eager bundle.
 * 2. **The column header row is a bar, not the real labels.** The headers are
 *    static, but they live inside the lazy chunk's column defs together with
 *    their flex widths; copying the strings here would drift, and copying them
 *    *without* the widths would render a header that lines up with nothing.
 */
import { Filter, RefreshCcw } from 'lucide-react'

import { AIRailLayout } from '@/features/ai/components/panel/ai-rail-layout'
import { useCasePageContext } from '@/features/air/case-management/ai/hooks/use-case-page-context'
import { Section } from '@/features/app-shell/section'

import { TableSearch } from '@/shared/data-table/components/table-search'
import { TableToolbar } from '@/shared/data-table/components/table-toolbar'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

import { CaseFilterList } from './case-filter-list'

/**
 * Mirrors `TableLastUpdated` — the only part of the toolbar that needs data.
 *
 * The container classes are the real component's base merged with the override
 * `table.tsx` passes it; keep the two in step or the toolbar's right edge moves
 * when the query lands. Refresh renders real-but-disabled: it exists, it's just
 * nothing to refresh yet.
 */
const CaseLastUpdatedSkeleton = () => (
	<div className="flex w-full shrink-0 grow-0 items-center gap-4 self-start pt-2 sm:w-fit sm:justify-end sm:self-auto">
		<p className="text-muted-foreground">
			{/* Sized to "less than a minute ago" — the string that renders the
			    instant this skeleton is replaced. Too narrow and the whole toolbar
			    re-flows: last-updated is the flex row's fixed end, so every pixel
			    it lacks is a pixel the search input steals. */}
			Last updated:{' '}
			{/* `Skeleton` renders a `div`, which is invalid inside a `p` — the
			    placeholder is a plain span styled to match it. */}
			<span className="bg-muted inline-block h-4 w-44 animate-pulse rounded-md align-middle" />
		</p>

		<Button
			variant="outline"
			size="icon"
			disabled
			title="Refresh"
			className="h-8 w-8"
		>
			<RefreshCcw className="h-4 w-4" />
		</Button>
	</div>
)

/**
 * The toolbar row: filters trigger, search, last-updated.
 *
 * `TableSearch` is the real component, not a placeholder — it reads and writes
 * only the URL, so it keeps working while the table loads and whatever the user
 * types is already applied by the time rows arrive.
 */
export const CaseToolbarSkeleton = () => (
	<TableToolbar className="w-full">
		<div className="flex w-full flex-row items-start justify-start gap-2">
			{/* Look-alike for `CaseManagementFilters` — see the file header. */}
			<Button
				variant="outline"
				disabled
				className="flex h-12 flex-row items-center gap-2"
			>
				<Filter className="size-4" /> Filters
			</Button>

			<TableSearch
				placeholder="Search for name"
				analyticsEvent="air_case_management_search"
			/>
		</div>

		<CaseLastUpdatedSkeleton />
	</TableToolbar>
)

type CaseTableSkeletonProps = {
	/** Enough rows to overflow the viewport; the container clips the excess. */
	rows?: number
}

/**
 * Mirrors the `DataTable` region.
 *
 * `flex-1 min-h-0` rather than a fixed block: the real table grows into the
 * remaining page height, so a short skeleton would let everything below it jump
 * upward. Row height tracks `ROW_HEIGHT` (48px) in
 * `shared/data-table/components/data-table.tsx`; the header bar tracks
 * `TableHead`'s `h-10`.
 */
export const CaseTableSkeleton = ({ rows = 14 }: CaseTableSkeletonProps) => (
	<div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
		<Skeleton className="h-10 w-full shrink-0 rounded-none" />

		{Array.from({ length: rows }, (_, index) => (
			<Skeleton
				key={index}
				className="mt-px h-12 w-full shrink-0 rounded-none"
			/>
		))}
	</div>
)

/**
 * The page-level composite: the route's `pendingComponent` *and* the list
 * section's `Suspense` fallback.
 *
 * One definition serving both call sites — they fire at different moments
 * (route transition vs. lazy chunk + query) but must look identical, and two
 * hand-written copies drift. The wrapper repeats `table.tsx`'s own wrapper
 * classes, gaps included.
 */
export const CaseManagementSkeleton = () => (
	<div className="flex h-full w-full flex-col items-start justify-start gap-6">
		<CaseToolbarSkeleton />

		{/* Real component: pure URL state. Chips the user filtered by stay put
		    instead of appearing a beat later and pushing the table down. */}
		<CaseFilterList />

		<CaseTableSkeleton />
	</div>
)

/**
 * Case DETAIL's loading shapes — the `$id` route's per-section `Suspense`
 * fallbacks (`DetailsTitle` / `DetailsHeaderCluster` / `DetailsTabs`) and the
 * page-level `pendingComponent` that composes them. Read each next to the
 * component it mirrors under `./details/`.
 */

/**
 * Mirrors `DetailsTitle`: back button, title + description stack, and the
 * status-select/ask-AI cluster pinned to the end via the same `flex-auto`
 * spacer the real component uses.
 */
export const DetailsTitleSkeleton = () => (
	<div className="flex w-full flex-col items-start justify-start gap-4 sm:flex-row">
		<Skeleton className="h-10 w-10 shrink-0 rounded-md" />

		<div className="flex w-full flex-col items-start justify-start gap-2">
			<Skeleton className="h-7 w-64" />
			<Skeleton className="h-4 w-96" />
		</div>

		<div className="flex-auto" />

		<div className="flex items-center gap-2">
			<Skeleton className="h-9 w-32" />
			<Skeleton className="h-9 w-24" />
		</div>
	</div>
)

/**
 * Mirrors `DetailsHeaderCluster`: the status pill, severity pill, and the
 * four icon-label fields, wrapping on the same `flex-wrap` row.
 */
export const DetailsHeaderClusterSkeleton = () => (
	<div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-2">
		<Skeleton className="h-6 w-20 rounded-full" />
		<Skeleton className="h-6 w-16 rounded-full" />
		<Skeleton className="h-5 w-24" />
		<Skeleton className="h-5 w-28" />
		<Skeleton className="h-5 w-32" />
		<Skeleton className="h-5 w-36" />
	</div>
)

/**
 * Mirrors `DetailsTabs`'s `h-10` tab strip — bar count matches the
 * SKU-on tab set (Behavioral Insights, Case Details, Evidence, Investigation
 * Steps, Automation, Logs, Case Notes); SKU-off cases render fewer real tabs,
 * so this is the upper bound, not an exact count.
 */
export const DetailsTabsSkeleton = () => (
	<div className="flex h-10 max-w-full items-start justify-start gap-6 overflow-x-auto overflow-y-hidden">
		<Skeleton className="h-8 w-28" />
		<Skeleton className="h-8 w-24" />
		<Skeleton className="h-8 w-20" />
		<Skeleton className="h-8 w-28" />
		<Skeleton className="h-8 w-24" />
		<Skeleton className="h-8 w-16" />
		<Skeleton className="h-8 w-20" />
	</div>
)

/**
 * The `$id` route's `pendingComponent`. `Outlet` (the tab's own nested route,
 * which has its own pending state) and `LogPanelMount` (a `null` Suspense
 * fallback — nothing to draw) render above the fold in `RouteComponent` only,
 * so they aren't part of this shape.
 *
 * `AIRailLayout` wraps this for the same reason the real page's wrapper is part
 * of every other composite: the rail is static chrome that needs no server
 * response, so drawing it only after the case resolves makes it pop in and
 * re-flows the content column. `useCasePageContext` reads route params only, so
 * it resolves during the pending state without suspending.
 */
export const CaseDetailsSkeleton = () => {
	const pageContext = useCasePageContext()

	return (
		<AIRailLayout pageContext={pageContext}>
			<Section>
				<div className="bg-background flex flex-col gap-4 pt-2 pb-3">
					<DetailsTitleSkeleton />
					<DetailsHeaderClusterSkeleton />
					<DetailsTabsSkeleton />
				</div>
			</Section>
		</AIRailLayout>
	)
}
